import { truncateBySentences } from 'lib/util';
import { filter, isEmpty, isNil, map, orderBy, slice } from 'lodash-es';
import {
  SailthruDataContentType,
  SailthruFormattedItem,
  SailthruItem,
  SailthruSections,
  sections
} from './types';

/**
 * Normalizes a timestamp to milliseconds, handling both string and number inputs.
 * If the timestamp is in seconds (10 digits), converts it to milliseconds.
 *
 * This normalization is needed because Sailthru sends timestamps in both string and number formats,
 * requiring us to handle both cases consistently.
 *
 * @param timestamp - The timestamp to normalize, either as a string or number
 * @returns The normalized timestamp in milliseconds
 */
const normalizeTimestamp = (timestamp: number | string): number => {
  const numericTimestamp =
    typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;

  const isSeconds = numericTimestamp.toString().length === 10;
  const milliseconds = isSeconds ? numericTimestamp * 1000 : numericTimestamp;

  return milliseconds;
};

// Types moved to ./types.ts to avoid circular dependencies

/**
 * Formats Sailthru data into a SailthruFormattedItem array.
 *
 * @param data - Array of SailthruItem objects to format
 * @returns Array of formatted SailthruFormattedItem objects
 */
export const formatSailthruData = (
  data: SailthruItem[]
): SailthruFormattedItem[] => {
  if (isEmpty(data)) return [];

  const formattedData = map(data, item => {
    if (!item?.url || isNil(item)) return null;

    const { title, url, description } = item;
    const formattedDescription = truncateBySentences(description, 2);
    const urlParts = url.split('/');
    const id = urlParts[urlParts.length - 1];

    return {
      id,
      title,
      description: formattedDescription,
      canonicalUrl: url,
      publishedAt: normalizeTimestamp(item.date),
      duration: item.vars?.duration * 60,
      presenterDisplayName: item.vars?.presenter_display_name,
      viewedCount: item?.views || 0,
      topics: {
        nodes: {
          ...map(item?.tags, tag => ({ name: tag }))
        }
      },
      primaryImageSet: [
        {
          url: item.image,
          aspectRatioName: '16:9'
        }
      ],
      slug: id,
      type: {
        name: item.vars?.talk_type
      }
    };
  });

  return orderBy(
    filter(formattedData, Boolean),
    ['publishedAt'],
    ['desc']
  ) as SailthruFormattedItem[];
};

/**
 * Fetches Sailthru data for all sections and formats it into a SailthruDataContentType object.
 *
 * @returns Promise<SailthruDataContentType> Object containing Sailthru data for each section
 *
 * @example
 * ```ts
 * const sailthruData = await fetchSailthruData();
 * ```
 */
export const fetchSailthruData = async (): Promise<SailthruDataContentType> => {
  const data = Object.values(SailthruSections).map(async sectionKey => {
    const sectionId = sections[sectionKey];
    const response = await fetch(
      `https://api.sail-personalize.com/v1/personalize?sections=${sectionId}`,
      {
        headers: {
          Authorization: `Bearer ${process.env.SAILTHRU_KEY}`
        }
      }
    );
    const data = await response.json();
    const recommendations =
      data?.sections?.[sectionId].json &&
      JSON.parse(data?.sections?.[sectionId].json);

    const formattedData = formatSailthruData(recommendations);
    return { sectionKey, data: formattedData };
  });

  const resolvedData = await Promise.all(data);

  const responseData = resolvedData.reduce<SailthruDataContentType>(
    (acc, { sectionKey, data }) => {
      acc[sectionKey] = data;
      return acc;
    },
    {
      [SailthruSections.Recommendations]: [],
      [SailthruSections.RecommendationsAI]: [],
      [SailthruSections.RecommendationsPsychology]: [],
      [SailthruSections.RecommendationsTEDEd]: [],
      [SailthruSections.RecommendationsPopular]: [],
      [SailthruSections.RecommendationsCountdown]: [],
      [SailthruSections.RecommendationsPodcast]: [],
      [SailthruSections.RecommendationsTrending]: [],
      [SailthruSections.RecommendationsNewest]: []
    } as SailthruDataContentType
  );

  return responseData;
};

export const orderSailthruContent = (
  content: SailthruFormattedItem[],
  limit: number = 4
): SailthruFormattedItem[] => {
  return slice(orderBy(content, ['publishedAt'], ['desc']), 0, limit);
};

// Export the new enrichment utility functions
export {
  correctSailthruItemSlug,
  correctSailthruItemsSlugs,
  enrichSailthruItemsWithGraphQL,
  enrichSailthruItemWithGraphQL,
  getEnrichedVideoData,
  getEnrichedVideoDataBatch,
  getTalkSlug,
  getTalkSlugs
} from './getTalkSlug';

// Export other utilities
export { deduplicateSailthruContent } from './dedupeContent';
export { useFallbackData } from './fallback';
// Import enum and sections from types file to avoid circular dependencies
export type { EnrichedVideoData } from './getTalkSlug';
export { SailthruSections, sections } from './types';
export type {
  Recommendation,
  SailthruDataContentType,
  SailthruDataContentTypeSpecific,
  SailthruFormattedItem
} from './types';
